
import { useState } from 'react';

const Home = () => {
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState(false);
  const [consultationFormData, setConsultationFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [presentationFormData, setPresentationFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    employees: '',
    machines: '',
    message: ''
  });

  const handleConsultationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    Object.entries(consultationFormData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d3objbb9pnvr10115ms0', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });
      
      if (response.ok) {
        alert('お申し込みありがとうございます。担当者よりご連絡いたします。');
        setIsConsultationModalOpen(false);
        setConsultationFormData({ company: '', name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  const handlePresentationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    Object.entries(presentationFormData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d3objbb9pnvr10115msg', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });
      
      if (response.ok) {
        alert('お申し込みありがとうございます。担当者よりご連絡いたします。');
        setIsPresentationModalOpen(false);
        setPresentationFormData({ company: '', name: '', email: '', phone: '', employees: '', machines: '', message: '' });
      }
    } catch (error) {
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/2164e1b7672604a4b6f40cc0551a0838/eb8ff83a3546fe57070bf812bafc35d3.jpeg"
                alt="ZOLLER"
                className="h-10 w-auto"
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">機能</a>
              <a href="#packages" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">パッケージ</a>
              <a href="#cases" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">事例</a>
              <a href="#faq" className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer">FAQ</a>
            </nav>
            <button 
              onClick={() => setIsConsultationModalOpen(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              無料相談
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/3e7759330bfcf18624864b7dd298e8d3.jpg"
            alt="Manufacturing facility"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="w-full">
              <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                工具管理を"書棚化"するより、<br />
                <span className="text-blue-600">"利益装置化"</span>する。
              </h1>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                ZOLLER TMS｜プリセッターメーカーが作った"稼ぐための"工具マネジメント
              </h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                「探す・間違う・やり直す」をゼロに近づけ、稼働率を上げて投資を回収する。<br />
                プリセッターとネイティブ連携する唯一の工具管理プラットフォームが、現場の段取りと品質を同時に最短ルートで最適化。
              </p>
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
              >
                無料: 20分クイックweb相談を予約
              </button>
            </div>
            <div className="hidden lg:block">
              <img 
                src="https://readdy.ai/api/search-image?query=Advanced%20tool%20management%20dashboard%20interface%20showing%20real-time%20production%20metrics%2C%203D%20tool%20assembly%20visualization%2C%20inventory%20tracking%20system%20with%20QR%20codes%2C%20modern%20industrial%20software%20interface%20with%20blue%20and%20orange%20accents%2C%20clean%20professional%20design&width=600&height=500&seq=hero-dashboard&orientation=landscape"
                alt="ZOLLER TMS Dashboard"
                className="w-full h-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              なぜ今、工具"管理"ではなく、工具"収益化"か？
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-red-50 p-8 rounded-lg border-l-4 border-red-500">
              <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">最も高いコスト</h3>
              <p className="text-gray-700">現場で最も高いコストは「機械が動いていない時間」。</p>
            </div>
            <div className="bg-yellow-50 p-8 rounded-lg border-l-4 border-yellow-500">
              <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">隠れた停止要因</h3>
              <p className="text-gray-700">工具の取り違い・組立ミス・測定データ転記ミスは、隠れた停止と手戻りを生む。</p>
            </div>
            <div className="bg-blue-50 p-8 rounded-lg border-l-4 border-blue-500">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-file-excel-2-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">限界のある管理</h3>
              <p className="text-gray-700">Excelと属人ワザでは、機械台数×オプスの拡大に耐えられない。</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-6">ZOLLER TMSは、</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold">1</span>
                </div>
                <p>プリセッター（venturion / smile）とネイティブ連携</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold">2</span>
                </div>
                <p>セットアップシートと測定データを"そのまま"NCへ橋渡し</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-sm font-bold">3</span>
                </div>
                <p>在庫・寿命・ロケーション・組立手順を1枚の画面で同期</p>
              </div>
            </div>
            <p className="mt-6 text-lg">することで、停止要因を潰し、稼働率を底上げします。</p>
          </div>
        </div>
      </section>

      {/* Differentiation Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              "ZOLLERだからこそ"の"盲点"を突く差別化
            </h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-400 rounded-lg flex items-center justify-center">
                    <i className="ri-file-list-3-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">一般的な工具管理</h3>
                    <p className="text-gray-700">「棚卸・台帳」に強い。</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <i className="ri-ruler-2-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">ZOLLERの強み</h3>
                    <p className="text-gray-700">"測る側"の作り手。測定→組立→転送まで一気通貫。</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                    <i className="ri-line-chart-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">結果</h3>
                    <p className="text-gray-700">"3％稼働率UP"の前に起きる微停止要因（探す/迷う/間違う）を構造ごと解消。</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Precision%20measurement%20equipment%20and%20tool%20presetter%20machine%20in%20modern%20manufacturing%20environment%2C%20ZOLLER%20presetter%20with%20digital%20display%2C%20organized%20tool%20storage%20system%20with%20QR%20codes%2C%20professional%20industrial%20setting%20with%20blue%20lighting&width=600&height=500&seq=presetter-equipment&orientation=landscape"
                alt="ZOLLER Presetter Equipment"
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              数字で見る"即効性"：ミニ試算（例）
            </h2>
            <p className="text-lg text-gray-700">
              前提：機械チャージ ¥15,000/時間、10台、1日16時間稼働、稼働日240日/年
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-green-50 p-8 rounded-lg text-center border-2 border-green-200">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-arrow-up-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">稼働率向上</h3>
              <p className="text-3xl font-bold text-green-600 mb-2">+2.0％</p>
              <p className="text-lg font-semibold text-gray-900">年間 +¥11,520,000</p>
              <p className="text-sm text-gray-600 mt-2">（10台×16h×240日×2％×¥15,000）</p>
            </div>
            
            <div className="bg-blue-50 p-8 rounded-lg text-center border-2 border-blue-200">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">段取り短縮</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">10分/ジョブ</p>
              <p className="text-lg font-semibold text-gray-900">年間 +¥7,700,000</p>
              <p className="text-sm text-gray-600 mt-2">（合計約160h/月×¥4,000×12ヶ月）</p>
            </div>
            
            <div className="bg-orange-50 p-8 rounded-lg text-center border-2 border-orange-200">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">不良・手戻り削減</h3>
              <p className="text-3xl font-bold text-orange-600 mb-2">1.0％→0.5％</p>
              <p className="text-lg font-semibold text-gray-900">年間 数百万円規模</p>
              <p className="text-sm text-gray-600 mt-2">材料/工数ロスの縮減</p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">合計インパクト：年¥2,000万級も現実的</h3>
            <p className="text-lg mb-6">（規模に応じて変動）</p>
            <p className="text-xl">投資回収の起点は「在庫と手戻りの削減」よりも、"微停止の連鎖を断つ"こと。</p>
            <button 
              onClick={() => setIsPresentationModalOpen(true)}
              className="mt-6 bg-white text-orange-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              ZOLLER TMSのwebプレゼン(無料)を受けてみる
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              主要機能（現場の"停止要因"に直結）
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">組立ミス防止</h3>
              <p className="text-gray-700">3D/図番ベースの組立テンプレ、互換チェック、トルク/突出長ガイド</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-links-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">プリセッター連携</h3>
              <p className="text-gray-700">測定→セットアップシート自動生成→NC/機上へデータ転送</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-archive-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">工具寿命・在庫</h3>
              <p className="text-gray-700">ロット/ロケーション、最小在庫・発注点アラート、QR/ラベル発行</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-search-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">トレーサビリティ</h3>
              <p className="text-gray-700">誰が、いつ、何を組み、どの機械で使ったかを逆引き可能</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-dashboard-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">ダッシュボード</h3>
              <p className="text-gray-700">稼働時間ロス要因、段取り時間、在庫回転、工具寿命の見える化</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-share-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">連携</h3>
              <p className="text-gray-700">ERP/MES/購買システム（CSV/API）、マルチ拠点・多言語</p>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">パッケージと適合</h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Bronze Package */}
            <div className="bg-gradient-to-b from-amber-50 to-amber-100 p-8 rounded-lg border-2 border-amber-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-amber-800 mb-2">ブロンズ</h3>
                <p className="text-amber-700">小～中規模</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">目的</h4>
                  <p className="text-sm text-gray-700">工具台帳と棚卸の標準化</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">中心機能</h4>
                  <p className="text-sm text-gray-700">台帳/ロケーション、QR/ラベル、基本検索、在庫・発注点</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">導入範囲</h4>
                  <p className="text-sm text-gray-700">単一工場/ツール室主導</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">期間目安</h4>
                  <p className="text-sm text-gray-700">2–4週間</p>
                </div>
              </div>
            </div>
            
            {/* Silver Package */}
            <div className="bg-gradient-to-b from-gray-50 to-gray-100 p-8 rounded-lg border-2 border-gray-300 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">おすすめ</span>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">シルバー</h3>
                <p className="text-gray-700">中～大規模</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">目的</h4>
                  <p className="text-sm text-gray-700">段取り・測定・転送まで一気通貫</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">中心機能</h4>
                  <p className="text-sm text-gray-700">組立テンプレ、プリセッター連携、セットアップシート、寿命管理、ツールセット管理、ダッシュボード（基本）</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">導入範囲</h4>
                  <p className="text-sm text-gray-700">ツール室＋生産/品質</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">期間目安</h4>
                  <p className="text-sm text-gray-700">6–10週間</p>
                </div>
              </div>
            </div>
            
            {/* Gold Package */}
            <div className="bg-gradient-to-b from-yellow-50 to-yellow-100 p-8 rounded-lg border-2 border-yellow-300">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-yellow-800 mb-2">ゴールド</h3>
                <p className="text-yellow-700">大/多拠点</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">目的</h4>
                  <p className="text-sm text-gray-700">全社展開・ERP連携・監査対応</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">中心機能</h4>
                  <p className="text-sm text-gray-700">API/ERP/MES連携、マルチ拠点、役割別ワークフロー、監査ログ、詳細ダッシュボード、トレーサビリティ完全版</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">導入範囲</h4>
                  <p className="text-sm text-gray-700">事業所横断・本社標準</p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">期間目安</h4>
                  <p className="text-sm text-gray-700">10–16週間</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-700 mb-6">
              アドオン：クラウド閲覧（webTMS）、追加ユーザ、追加ダッシュボード、特殊API、バーコード/棚用ハード。
            </p>
            <button 
              onClick={() => setIsPresentationModalOpen(true)}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              ZOLLER TMSのwebプレゼン(無料)を受けてみる
            </button>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="cases" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">参考事例</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">「一発良品」定着</h3>
              <p className="text-gray-700 text-center">組立テンプレ＋測定連携で試し削り1回に圧縮</p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-search-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">"探す時間"ゼロ化</h3>
              <p className="text-gray-700 text-center">ロケーションとQRで新人でも3分以内にピックアップ可能に。</p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shopping-cart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">購買の先手</h3>
              <p className="text-gray-700 text-center">最小在庫割れをアラート→計画購買でコスト3–7％削減</p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">導入と"使いこなし"保証</h2>
          </div>
          
          <div className="space-y-8">
            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">データ整備スプリント（1–2週）</h3>
                <p className="text-gray-700">命名規則・基本属性をテンプレで一括登録</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">現場別ビュー設定（1週）</h3>
                <p className="text-gray-700">オペレータは"今日使う画面だけ"表示</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">段取りテンプレ作成（1–2週）</h3>
                <p className="text-gray-700">上位20ツールセットから</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">4</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">30日伴走トレーニング（Web講習）</h3>
                <p className="text-gray-700">実ジョブで操作→KPIを計測して改善</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">5</span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">90日レビュー</h3>
                <p className="text-gray-700">稼働率・段取り時間・不良率の実測比較をレポート</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">よくある質問</h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. UIが難しそうです…</h3>
              <p className="text-gray-700">A. 初期は現場別の最小画面だけで運用。段取り担当は"組立→測定→転送"の3クリック導線です。</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. 既存の台帳/Excelを活かせますか？</h3>
              <p className="text-gray-700">A. はい。CSV取込テンプレで8割以上は一括移行。残りは当社が整備支援します。</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. ERPやMESとつながりますか？</h3>
              <p className="text-gray-700">A. ゴールドでAPI/CSV連携に対応。購買/原価/品質のシステムとも橋渡し可能な場合があります。具体的にご相談いただければ実績等確認いたします。</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Q. プリセッター連携のメリットは？</h3>
              <p className="text-gray-700">A. 測定値の二重入力ゼロ／組立ミス予防／セットアップ時間短縮。停止要因の根本を断てます。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Low Risk Start Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-8">今すぐできる"低リスク"の始め方</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-xl mb-8 leading-relaxed">
              ブロンズで台帳とロケーションを即整備 → シルバーで段取りと測定に拡張 → 必要に応じてゴールドで全社化。<br />
              まずは1ライン/1セルで90日。数字が出たら横展開が最速です。
            </p>
            <button 
              onClick={() => setIsPresentationModalOpen(true)}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              ZOLLER TMSのwebプレゼン(無料)を受けてみる
            </button>
          </div>
        </div>
      </section>

      {/* P.S. Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">追伸（P.S.）</h2>
          <p className="text-xl mb-6 leading-relaxed">
            多くの現場で、"3％UPの前にある1％の微停止"が放置されています。
          </p>
          <p className="text-xl mb-8 leading-relaxed">
            ZOLLER TMSは「棚卸ツール」ではなく、微停止を消して機械を回し切るための"利益装置"です。<br />
            まずは1ラインで、数字を出しましょう。
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/2164e1b7672604a4b6f40cc0551a0838/eb8ff83a3546fe57070bf812bafc35d3.jpeg"
                alt="ZOLLER"
                className="h-12 w-auto mb-4"
              />
              <p className="text-gray-300">プリセッターメーカーが作った"稼ぐための"工具マネジメント</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">お問い合わせ</h4>
              <p className="text-gray-300 mb-2">ZOLLER Japan株式会社</p>
              <p className="text-gray-300">info@zoller-jp.com</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">メニュー</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="text-gray-300 hover:text-white transition-colors cursor-pointer">機能</a></li>
                <li><a href="#packages" className="text-gray-300 hover:text-white transition-colors cursor-pointer">パッケージ</a></li>
                <li><a href="#cases" className="text-gray-300 hover:text-white transition-colors cursor-pointer">事例</a></li>
                <li><a href="#faq" className="text-gray-300 hover:text-white transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 ZOLLER Japan株式会社. All rights reserved. | 
              <a href="https://readdy.ai/?origin=logo" className="text-gray-300 hover:text-white ml-2 cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>

      {/* Consultation Modal */}
      {isConsultationModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">無料Web相談お申し込み</h3>
                <button 
                  onClick={() => setIsConsultationModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              
              <form id="consultation-form" data-readdy-form onSubmit={handleConsultationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">会社名 *</label>
                  <input 
                    type="text" 
                    name="company"
                    value={consultationFormData.company}
                    onChange={(e) => setConsultationFormData({...consultationFormData, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">お名前 *</label>
                  <input 
                    type="text" 
                    name="name"
                    value={consultationFormData.name}
                    onChange={(e) => setConsultationFormData({...consultationFormData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">メールアドレス *</label>
                  <input 
                    type="email" 
                    name="email"
                    value={consultationFormData.email}
                    onChange={(e) => setConsultationFormData({...consultationFormData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">電話番号</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={consultationFormData.phone}
                    onChange={(e) => setConsultationFormData({...consultationFormData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ご相談内容</label>
                  <textarea 
                    name="message"
                    value={consultationFormData.message}
                    onChange={(e) => setConsultationFormData({...consultationFormData, message: e.target.value})}
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="現在の課題や相談したい内容をお聞かせください"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">500文字以内</p>
                </div>
                
                <button 
                  type="submit"
                  className="w-full bg-orange-500 text-white py-3 rounded-md hover:bg-orange-600 transition-colors font-semibold whitespace-nowrap cursor-pointer"
                >
                  相談を申し込む
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Presentation Modal */}
      {isPresentationModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Webプレゼンお申し込み</h3>
                <button 
                  onClick={() => setIsPresentationModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              
              <form id="presentation-form" data-readdy-form onSubmit={handlePresentationSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">会社名 *</label>
                  <input 
                    type="text" 
                    name="company"
                    value={presentationFormData.company}
                    onChange={(e) => setPresentationFormData({...presentationFormData, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">お名前 *</label>
                  <input 
                    type="text" 
                    name="name"
                    value={presentationFormData.name}
                    onChange={(e) => setPresentationFormData({...presentationFormData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">メールアドレス *</label>
                  <input 
                    type="email" 
                    name="email"
                    value={presentationFormData.email}
                    onChange={(e) => setPresentationFormData({...presentationFormData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required 
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">電話番号</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={presentationFormData.phone}
                    onChange={(e) => setPresentationFormData({...presentationFormData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">従業員数</label>
                  <select 
                    name="employees"
                    value={presentationFormData.employees}
                    onChange={(e) => setPresentationFormData({...presentationFormData, employees: e.target.value})}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="1-10名">1-10名</option>
                    <option value="11-50名">11-50名</option>
                    <option value="51-100名">51-100名</option>
                    <option value="101-500名">101-500名</option>
                    <option value="500名以上">500名以上</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">機械台数</label>
                  <select 
                    name="machines"
                    value={presentationFormData.machines}
                    onChange={(e) => setPresentationFormData({...presentationFormData, machines: e.target.value})}
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">選択してください</option>
                    <option value="1-5台">1-5台</option>
                    <option value="6-10台">6-10台</option>
                    <option value="11-20台">11-20台</option>
                    <option value="21-50台">21-50台</option>
                    <option value="50台以上">50台以上</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ご質問・ご要望</label>
                  <textarea 
                    name="message"
                    value={presentationFormData.message}
                    onChange={(e) => setPresentationFormData({...presentationFormData, message: e.target.value})}
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="現在の課題や知りたい機能などをお聞かせください"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">500文字以内</p>
                </div>
                
                <button 
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 transition-colors font-semibold whitespace-nowrap cursor-pointer"
                >
                  プレゼンを申し込む
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
